import { Fragment, useCallback, useState, useEffect } from 'react'
import {
  List,
  SelectInput,
  DateInput,
  Datagrid,
  NumberField,
  TextField,
  ReferenceInput,
  AutocompleteInput,
  DateField,
  FunctionField,
  useListContext,
  useDataProvider,
} from 'react-admin'
import { Button, Grid } from '@mui/material'
import moment from 'moment'

import FilterComponent from '../../components/Filter'
import { FILTER_DATE, ROLES, SUBSCRIPTION } from '../../components/constants'
import { formatCurrency } from '../../utils/formatCurrency'
import { exportExcel } from '../../utils/exportExcel'

const exporter = records => {
  const data = records.map(record => {
    const { commission } = record
    const amountCommission =
      (commission / 100) * SUBSCRIPTION.YEARLY_PRICE * 0.9

    return {
      Code: record.code,
      Plan: record.productId.split('.').pop(),
      Discount: `${record.discount}%`,
      Price: record.amount,
      Commission: `${commission}%`,
      'Commission amount': amountCommission.toFixed(0),
      'Transaction ID': record.originalTransactionId,
      'Purchased at': new Date(record.createdAt).toLocaleDateString('vi-VN'),
      'Payment method': record.paymentMethod,
    }
  })

  exportExcel({
    data,
    title: `affiliate_dashboard_${moment().format('MM_DD_YYYY')}`,
    type: 'xlsx',
  })
}

const CustomFilter = props => {
  const { filterValues } = useListContext()
  return (
    <FilterComponent {...props}>
      <ReferenceInput
        label="Affiliate"
        source="affiliateId"
        reference="users"
        filter={{ role: ROLES.AFFILIATE }}
        alwaysOn>
        <AutocompleteInput
          optionText="name"
          filterToQuery={searchText => ({
            name: { $iLike: `%${searchText}%` },
            role: ROLES.AFFILIATE,
          })}
        />
      </ReferenceInput>
      <SelectInput
        label="Filter"
        source="date"
        choices={FILTER_DATE}
        alwaysOn></SelectInput>
      {'custom' === filterValues.date && (
        <DateInput label="From date" source="fromDate" alwaysOn></DateInput>
      )}
      {'custom' === filterValues.date && (
        <DateInput label="To date" source="toDate" alwaysOn></DateInput>
      )}
    </FilterComponent>
  )
}

const CustomDatagrid = props => {
  const dataProvider = useDataProvider()

  const [isShowRevenue, toggleShowRevenue] = useState(false)
  const [state, setState] = useState({
    usersRedeemed: 0,
    newUsersRedeemed: 0,
    totalCommission: 0,
  })

  const PlanTextField = useCallback(({ record = {} }) => {
    const { productId } = record
    if (!productId) return <span>-</span>
    const plan = productId.split('.').pop()
    return <span>{plan}</span>
  }, [])

  const CommissionAmountField = useCallback(({ record = {} }) => {
    const { commission } = record
    let amountCommission = (commission / 100) * SUBSCRIPTION.YEARLY_PRICE * 0.9
    return <span>{formatCurrency.format(amountCommission.toFixed(0))}</span>
  }, [])

  const { filterValues } = useListContext()
  useEffect(() => {
    dataProvider
      .getOne('affiliate-dashboard-revenue', {
        id: null,
        date: filterValues && filterValues.date,
        affiliateId: filterValues && filterValues.affiliateId,
        fromDate: filterValues && filterValues.fromDate,
        toDate: filterValues && filterValues.toDate,
      })
      .then(({ data }) =>
        setState(v => ({ ...v, totalCommission: data.result })),
      )
      .catch(console.log)
    dataProvider
      .getOne('affiliate-dashboard-install', {
        id: null,
        date: filterValues && filterValues.date,
        affiliateId: filterValues && filterValues.affiliateId,
        fromDate: filterValues && filterValues.fromDate,
        toDate: filterValues && filterValues.toDate,
      })
      .then(({ data }) => {
        setState(v => ({
          ...v,
          usersRedeemed: data.usersRedeemed,
          newUsersRedeemed: data.newUsersRedeemed,
          affiliate: data.affiliate,
        }))
      })
      .catch(console.log)
  }, [dataProvider, filterValues, props.filterValues])

  return (
    <Fragment>
      <div style={{ marginLeft: 12 }}>
        <Grid container>
          {state.affiliate && (
            <Grid item xs={12}>
              <h3>Name: {state.affiliate.name}</h3>
              <h3>Web link: {state.affiliate.webLink}</h3>
            </Grid>
          )}
          <Grid item xs={12}>
            <h3>Users redeemed a free book: {state.usersRedeemed}</h3>
            <h3>New users redeemed a free book: {state.newUsersRedeemed}</h3>
          </Grid>
          {isShowRevenue ? (
            <>
              <Grid item xs={12}>
                <Button
                  color="primary"
                  onClick={() => toggleShowRevenue(false)}>
                  Hide details
                </Button>
                <h3>
                  Total commission:{' '}
                  {formatCurrency.format(state.totalCommission)}
                </h3>
              </Grid>
              <Datagrid bulkActionButtons={false} rowClick={false} {...props}>
                <TextField source="code" />
                <PlanTextField source="Plan" />
                <FunctionField
                  label="Discount"
                  render={record => `${record.discount}%`}
                />
                <NumberField source="amount" label="Price (after discount)" />
                <FunctionField
                  label="Commission"
                  render={record => `${record.commission}%`}
                />
                <CommissionAmountField
                  source="amount"
                  label="Commission amount"
                />
                <TextField
                  source="originalTransactionId"
                  label="Transaction Id"
                />
                <DateField source="createdAt" label="Purchased at" />
                <TextField source="paymentMethod" label="Payment method" />
              </Datagrid>
            </>
          ) : (
            <Grid item xs={12}>
              <Button color="primary" onClick={() => toggleShowRevenue(true)}>
                Show details
              </Button>
            </Grid>
          )}
        </Grid>
      </div>
    </Fragment>
  )
}

const AffiliateDashboard = props => (
  <List
    filters={<CustomFilter />}
    {...props}
    filterDefaultValues={{
      date: 'all',
    }}
    exporter={exporter}>
    <CustomDatagrid />
  </List>
)

export default AffiliateDashboard
